import { format } from "date-fns";

import MutuelleLookup from "../MutuelleLookup";
import ConflictChoice from "./ConflictChoice";
import TextInput from "./TextInput";
import { LabeledCell } from "./PatientSection";
import { FIELD_STATE, isRequiredFieldState } from "../../utils";

const MutuelleSection = ({ admission, activeFields, validationErrors = [], onAdmissionChange, isMerging, conflicts, onConflictChoose, ProcessingStatusIcon }) => {
  const mutuelle = admission?.patient?.mutuelle;
  const isMissing = (state, filled) => !isMerging && isRequiredFieldState(state) && !filled;
  const conflictOf = (key) => conflicts.find((conflict) => conflict.key === key);
  const isChoicePending = (key) => conflictOf(key) !== undefined && conflictOf(key).choice === undefined;
  const isLockedByMatch = (key) => isMerging && conflictOf(key) === undefined;
  const today = format(new Date(), "yyyy-MM-dd");
  const rightsFromNotStarted = !!mutuelle?.rightsFrom && mutuelle.rightsFrom > today;
  const rightsToExpired = !!mutuelle?.rightsTo && mutuelle.rightsTo < today;

  return (
    <div className="mb-4">
      <div className="mb-2">
        <div className="section-title">
          <span>Mutuelle</span>
          <ProcessingStatusIcon type="MUTUELLE" referenceId={admission?.patient?._id} />
        </div>
      </div>
      <div className={`card relative overflow-hidden transition-shadow ${isMerging ? "shadow-[0_0_0_1px_rgba(16,24,40,0.06),0_20px_48px_-16px_rgba(16,24,40,0.28)]" : ""}`}>
        <div className={`grid items-center ${isMerging ? "grid-cols-2" : "grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] divide-x divide-gray-100"}`}>
          <div className="p-4">
            <LabeledCell
              label="Mutuelle"
              dataFocusField="patient.mutuelle.code"
              attention={isChoicePending("mutuelle.code") || isMissing(activeFields?.mutuelle?.code, !!mutuelle?.code)}
              locked={isLockedByMatch("mutuelle.code")}
            >
              {conflictOf("mutuelle.code") ? (
                <ConflictChoice conflict={conflictOf("mutuelle.code")} onChoose={onConflictChoose} />
              ) : (
                <MutuelleLookup
                  fieldState={activeFields?.mutuelle?.code}
                  initialMutuelle={mutuelle}
                  onMutuelleSelect={(nextMutuelle) => {
                    onAdmissionChange([
                      { field: "patient.mutuelle.code", value: nextMutuelle.code },
                      { field: "patient.mutuelle.name", value: nextMutuelle.name || "" },
                      { field: "patient.mutuelle.manual", value: nextMutuelle.manual || false },
                    ]);
                  }}
                  onMutuelleRemove={() => {
                    onAdmissionChange([
                      { field: "patient.mutuelle.code", value: "" },
                      { field: "patient.mutuelle.name", value: "" },
                      { field: "patient.mutuelle.manual", value: false },
                    ]);
                  }}
                  placeholder="Rechercher une mutuelle..."
                />
              )}
            </LabeledCell>
          </div>
          <div className="p-4">
            {activeFields?.mutuelle?.adherentNumber !== FIELD_STATE.DISABLE ? (
              <LabeledCell
                label="N° adhérent"
                dataFocusField="patient.mutuelle.adherentNumber"
                attention={isChoicePending("mutuelle.adherentNumber") || isMissing(activeFields?.mutuelle?.adherentNumber, !!mutuelle?.adherentNumber)}
                locked={isLockedByMatch("mutuelle.adherentNumber")}
              >
                {conflictOf("mutuelle.adherentNumber") ? (
                  <ConflictChoice conflict={conflictOf("mutuelle.adherentNumber")} onChoose={onConflictChoose} />
                ) : (
                  <TextInput
                    fieldState={activeFields?.mutuelle?.adherentNumber}
                    value={mutuelle?.adherentNumber}
                    onChange={(event) => onAdmissionChange([{ field: "patient.mutuelle.adherentNumber", value: event.target.value }])}
                    placeholder="—"
                  />
                )}
              </LabeledCell>
            ) : null}
          </div>
          <div className="p-4">
            {activeFields?.mutuelle?.rightsFrom !== FIELD_STATE.DISABLE ? (
              <LabeledCell
                label="Droits du"
                dataFocusField="patient.mutuelle.rightsFrom"
                attention={isChoicePending("mutuelle.rightsFrom") || isMissing(activeFields?.mutuelle?.rightsFrom, !!mutuelle?.rightsFrom)}
                locked={isLockedByMatch("mutuelle.rightsFrom")}
              >
                {conflictOf("mutuelle.rightsFrom") ? (
                  <ConflictChoice conflict={conflictOf("mutuelle.rightsFrom")} onChoose={onConflictChoose} />
                ) : (
                  <TextInput
                    type="date"
                    fieldState={activeFields?.mutuelle?.rightsFrom}
                    value={mutuelle?.rightsFrom}
                    hasError={validationErrors.includes("patient.mutuelle.rightsFrom")}
                    hasWarning={rightsFromNotStarted}
                    onChange={(event) => onAdmissionChange([{ field: "patient.mutuelle.rightsFrom", value: event.target.value }])}
                    placeholder="—"
                  />
                )}
              </LabeledCell>
            ) : null}
          </div>
          <div className="p-4">
            {activeFields?.mutuelle?.rightsTo !== FIELD_STATE.DISABLE ? (
              <LabeledCell
                label="Droits au"
                dataFocusField="patient.mutuelle.rightsTo"
                attention={isChoicePending("mutuelle.rightsTo") || isMissing(activeFields?.mutuelle?.rightsTo, !!mutuelle?.rightsTo)}
                locked={isLockedByMatch("mutuelle.rightsTo")}
              >
                {conflictOf("mutuelle.rightsTo") ? (
                  <ConflictChoice conflict={conflictOf("mutuelle.rightsTo")} onChoose={onConflictChoose} />
                ) : (
                  <TextInput
                    type="date"
                    fieldState={activeFields?.mutuelle?.rightsTo}
                    value={mutuelle?.rightsTo}
                    hasError={validationErrors.includes("patient.mutuelle.rightsTo")}
                    hasWarning={rightsToExpired}
                    onChange={(event) => onAdmissionChange([{ field: "patient.mutuelle.rightsTo", value: event.target.value }])}
                    placeholder="—"
                  />
                )}
              </LabeledCell>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MutuelleSection;
